import { createContext, useContext, useCallback, useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { FormProvider, useForm } from 'react-hook-form';

import useMemoObject from '@zenbusiness/application-commons-hooks/useMemoObject';
import useTimeout from '@zenbusiness/application-commons-hooks/useTimeout';
import { useIntl } from '@zenbusiness/application-commons-intl';
import SharedLottieAsset from '@zenbusiness/zui/SharedLottieAsset';

import InterstitialScreen from '#client/components/InterstitialScreen';
import useFlowState from '#client/hooks/flow/useFlowState';
import useFlowNavigation from '#client/hooks/useFlowNavigation';

const QuestionsContext = createContext(null);

const INTERSTITIAL_DELAY = 3000;
export function QuestionsProvider({ children }) {
  const { get, set } = useFlowState();
  const { goToNextPage } = useFlowNavigation();
  const businessInfo = useMemoObject(get('businessInfo') || {}, [get]);
  const [renderInsterstitial, setRenderInsterstitial] = useState(false);
  const { set: setTimeout, clear: clearTimeout } = useTimeout();
  const { formatMessage } = useIntl();

  useEffect(() => {
    return () => {
      clearTimeout();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formMethods = useForm({
    defaultValues: {
      businessExperience: businessInfo.businessExperience,
      businessStage: businessInfo.businessStage,
      formationCounty: businessInfo.formationCounty,
      name: businessInfo.name,
      jurisdictionState: businessInfo.jurisdictionState,
      industry: businessInfo.industry
    }
  });

  const onSubmit = useCallback(
    (data) => {
      set('businessInfo', (prev) => ({ ...prev, ...data }));
      setRenderInsterstitial(true);
    },
    [set, setRenderInsterstitial]
  );

  const value = useMemoObject({
    formMethods,
    onSubmit,
    renderInsterstitial
  });

  if (renderInsterstitial) {
    setTimeout(() => {
      goToNextPage();
    }, INTERSTITIAL_DELAY);

    const startComponent = (
      <SharedLottieAsset
        alt="bulding packages"
        height={128}
        src="/s2/animation/lottie/building-package-temp.json"
      />
    );

    return (
      <InterstitialScreen
        slotProps={{
          container: {
            spacing: 1,
            maxWidth: 215
          },
          title: {
            size: 'lg'
          }
        }}
        slots={{
          start: startComponent
        }}
        title={formatMessage({ id: 'shopping.page.questions.interstitial.title' })}
      />
    );
  }

  return (
    <QuestionsContext.Provider value={value}>
      <FormProvider {...formMethods}>{children}</FormProvider>
    </QuestionsContext.Provider>
  );
}

QuestionsProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export function useQuestions() {
  const context = useContext(QuestionsContext);
  if (!context) {
    throw new Error('useQuestions must be used within a QuestionsProvider');
  }
  return context;
}
